/**
 * Universal Size Selector Custom Element
 *
 * Provides functionality for switching between different size formats (US, UK, EU, CM)
 * and updating the variant selector display accordingly.
 * Integrates with the variant selector to handle size selection and form updates.
 * Supports fallback logic for UK/US sizes: size_uk/us -> kids_uk/us -> womens_uk/us -> mens_uk/us
 */

declare global {
  interface Window {
    $universalSizeMap: Record<string, {
      SIZE_UK: string;
      SIZE_US: string;
      SIZE_EU: string;
      SIZE_CM: string;
      AVAILABLE: boolean;
    }>;
  }
}

export class UniversalSizeSelector extends HTMLElement {
  private selectedSizeType: string = 'US'
  private sizeTypeButton: HTMLElement | null = null
  private sizeTypeLabel: HTMLElement | null = null
  private sizeTypeOptions: HTMLElement | null = null
  private sizeMetricInput: HTMLInputElement | null = null
  private productId: string = ''
  private isUpdatingStickyATC: boolean = false

  // Both of these attach to nodes OUTSIDE this element — the variant `select` elsewhere
  // in the form, and the sticky ATC label in another subtree entirely. They therefore
  // survive removal of this element and keep it alive through their closures, so they
  // have to be held on the instance and torn down explicitly.
  private stickyLabelObserver: MutationObserver | null = null
  private variantSelectEl: HTMLSelectElement | null = null
  private boundVariantChange: (() => void) | null = null
  private initTimeout: number | null = null

  connectedCallback(): void {
    this.init()
    this.attachEventListeners()
    this.updateSizeDisplay()
    this.initTimeout = window.setTimeout(() => {
      this.updateSizeMetric()
      this.updateStickyATC()
    }, 100)
  }

  disconnectedCallback(): void {
    this.stickyLabelObserver?.disconnect()
    this.stickyLabelObserver = null

    if (this.variantSelectEl && this.boundVariantChange) {
      this.variantSelectEl.removeEventListener('change', this.boundVariantChange)
    }
    this.variantSelectEl = null
    this.boundVariantChange = null

    if (this.initTimeout !== null) {
      window.clearTimeout(this.initTimeout)
      this.initTimeout = null
    }
  }

  private init(): void {
    this.sizeTypeButton = this.querySelector('[data-size-type-button]')
    this.sizeTypeLabel = this.querySelector('[data-size-type-label]')
    this.sizeTypeOptions = this.querySelector('[data-size-type-options]')
    this.sizeMetricInput = this.querySelector('[data-size-metric-input]')

    this.selectedSizeType = this.dataset.selectedSize || 'US'
    this.productId = this.dataset.productId || ''

    if (this.sizeTypeLabel) {
      this.sizeTypeLabel.textContent = this.selectedSizeType
    }
  }

  private attachEventListeners(): void {
    if (this.sizeTypeOptions) {
      this.sizeTypeOptions.addEventListener('click', (e) => {
        const button = (e.target as HTMLElement).closest('[data-size-type]') as HTMLElement
        if (button) {
          const newSizeType = button.dataset.sizeType
          if (newSizeType) {
            this.changeSizeType(newSizeType)
          }
        }
      })
    }

    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (variantGridContainer) {
      variantGridContainer.addEventListener('click', (e) => {
        const variantButton = (e.target as HTMLElement).closest('[data-variant-id]') as HTMLElement
        if (variantButton && variantButton.dataset.selectOption === 'true') {
          this.updateVariantActiveState(variantButton)
          this.updateSizeMetric()
          setTimeout(() => {
            this.updateStickyATC()
          }, 100)
        }
      })
    }

    const form = this.closest('form')
    if (form) {
      const variantSelect = form.querySelector<HTMLSelectElement>('select[name="id"]')
      if (variantSelect) {
        // Held on the instance so `disconnectedCallback` can remove it — an anonymous
        // arrow here could never be unregistered.
        this.variantSelectEl = variantSelect
        this.boundVariantChange = () => {
          setTimeout(() => {
            this.updateSizeMetric()
            this.updateStickyATC()
          }, 100)
        }
        variantSelect.addEventListener('change', this.boundVariantChange)
      }
    }

    this.watchStickyATCLabel()
  }

  private watchStickyATCLabel(): void {
    const stickySizeLabel = document.querySelector('[data-sticky-size-label]') as HTMLElement
    if (!stickySizeLabel) return

    // Stored on the instance rather than a local `const`, which was unreachable for
    // cleanup. This observes a node in the sticky ATC bar, outside this element.
    this.stickyLabelObserver?.disconnect()
    this.stickyLabelObserver = new MutationObserver(() => {
      if (this.isUpdatingStickyATC) return

      setTimeout(() => {
        this.updateStickyATC()
      }, 50)
    })

    this.stickyLabelObserver.observe(stickySizeLabel, {
      childList: true,
      characterData: true,
      subtree: true
    })
  }

  private changeSizeType(newSizeType: string): void {
    this.selectedSizeType = newSizeType

    if (this.sizeTypeLabel) {
      this.sizeTypeLabel.textContent = newSizeType
    }

    this.updateSizeTypeActiveState()
    this.updateSizeDisplay()
    this.updateSizeMetric()
    this.updateStickyATC()
  }

  private updateSizeTypeActiveState(): void {
    if (!this.sizeTypeOptions) return

    const buttons = this.sizeTypeOptions.querySelectorAll('[data-size-type]')
    buttons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const isActive = htmlButton.dataset.sizeType === this.selectedSizeType

      htmlButton.classList.toggle('font-bold', isActive)
      htmlButton.classList.toggle('bg-primary', isActive)
      htmlButton.classList.toggle('text-secondary', isActive)
    })
  }

  private updateSizeDisplay(): void {
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (!variantGridContainer || !window.$universalSizeMap) return

    const variantButtons = variantGridContainer.querySelectorAll('[data-variant-id]')

    variantButtons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const variantId = htmlButton.dataset.variantId

      if (variantId) {
        const variantData = window.$universalSizeMap[variantId]

        if (variantData) {
          let displaySize = ''

          switch (this.selectedSizeType) {
            case 'UK':
              displaySize = variantData.SIZE_UK
              break
            case 'EU':
              displaySize = variantData.SIZE_EU
              break
            case 'CM':
              displaySize = variantData.SIZE_CM
              break
            case 'US':
            default:
              displaySize = variantData.SIZE_US
              break
          }

          htmlButton.style.setProperty('--display-size', `"${displaySize}"`)
        }
      }
    })
  }

  private updateVariantActiveState(selectedButton: HTMLElement): void {
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (!variantGridContainer) return

    const allButtons = variantGridContainer.querySelectorAll('[data-variant-id]')

    allButtons.forEach((button) => {
      button.classList.remove('active')
    })

    selectedButton.classList.add('active')
  }

  private updateSizeMetric(): void {
    if (!this.sizeMetricInput) return

    const variantGridContainer = this.querySelector('[data-variant-grid]')
    const activeVariantButton = variantGridContainer?.querySelector('[data-variant-id].active') as HTMLElement
    if (!activeVariantButton) return

    const variantId = activeVariantButton.dataset.variantId
    if (!variantId) return

    const variantData = window.$universalSizeMap?.[variantId]
    if (!variantData) return

    let sizeValue = ''

    switch (this.selectedSizeType) {
      case 'UK':
        sizeValue = `UK ${variantData.SIZE_UK}`
        break
      case 'EU':
        sizeValue = `EURO ${variantData.SIZE_EU}`
        break
      case 'CM':
        sizeValue = `CM ${variantData.SIZE_CM}`
        break
      case 'US':
      default:
        sizeValue = `US ${variantData.SIZE_US}`
        break
    }

    this.sizeMetricInput.value = sizeValue
    this.updateStickyATC()
  }

  private updateStickyATC(): void {
    if (this.isUpdatingStickyATC) return
    this.isUpdatingStickyATC = true

    const stickySizeMetricInput = document.querySelector('[data-sticky-size-metric-input]') as HTMLInputElement
    const stickySizeLabel = document.querySelector('[data-sticky-size-text]') as HTMLElement

    if (!stickySizeMetricInput && !stickySizeLabel) {
      this.isUpdatingStickyATC = false
      return
    }

    const variantGridContainer = this.querySelector('[data-variant-grid]')
    let activeVariantButton = variantGridContainer?.querySelector('[data-variant-id].active') as HTMLElement

    if (!activeVariantButton) {
      const form = this.closest('form') || document.querySelector(`form[id*="${this.productId}"]`)
      if (form) {
        const variantSelect = form.querySelector('select[name="id"]') as HTMLSelectElement
        if (variantSelect && variantSelect.value) {
          activeVariantButton = variantGridContainer?.querySelector(`[data-variant-id="${variantSelect.value}"]`) as HTMLElement
        }
      }
    }

    // Each of these three bailouts must clear the re-entrancy guard, exactly as the
    // `stickySizeMetricInput` check above already does. They previously returned with it
    // still set, and because `connectedCallback` calls this 100ms after connect — before
    // the shopper has picked a size, so `activeVariantButton` is normally undefined — the
    // flag latched on virtually every page load. Every later call then bailed at the top
    // and the sticky add-to-cart size label never updated again for that session.
    //
    // Cleared inline rather than via `try/finally` on purpose: the success path at the
    // end defers clearing by 100ms to stop its own DOM writes re-entering through the
    // MutationObserver, and a `finally` would clear immediately and defeat that.
    if (!activeVariantButton) {
      this.isUpdatingStickyATC = false
      return
    }

    const variantId = activeVariantButton.dataset.variantId
    if (!variantId || !window.$universalSizeMap) {
      this.isUpdatingStickyATC = false
      return
    }

    const variantData = window.$universalSizeMap[variantId]
    if (!variantData) {
      this.isUpdatingStickyATC = false
      return
    }

    let sizeValue = ''

    switch (this.selectedSizeType) {
      case 'UK':
        sizeValue = `UK ${variantData.SIZE_UK}`
        break
      case 'EU':
        sizeValue = `EURO ${variantData.SIZE_EU}`
        break
      case 'CM':
        sizeValue = `CM ${variantData.SIZE_CM}`
        break
      case 'US':
      default:
        sizeValue = `US ${variantData.SIZE_US}`
        break
    }

    if (stickySizeMetricInput) {
      stickySizeMetricInput.value = sizeValue
    }

    const parentLabel = document.querySelector('[data-sticky-size-label]') as HTMLElement
    if (parentLabel) {
      let optionName = 'Size'
      const existingText = parentLabel.textContent || ''
      const match = existingText.match(/^(Size|Value):/)
      if (match) {
        optionName = match[1]
      } else {
        const variantSelector = parentLabel.closest('variant-selector')
        if (variantSelector) {
          const sizeLabel = variantSelector.querySelector('[size-selector-label]')
          if (sizeLabel) {
            const labelText = sizeLabel.textContent || ''
            const labelMatch = labelText.match(/^(Size|Value):/)
            if (labelMatch) {
              optionName = labelMatch[1]
            }
          }
        }
      }

      let stickySizeText = parentLabel.querySelector('[data-sticky-size-text]') as HTMLElement
      if (!stickySizeText) {
        stickySizeText = document.createElement('span')
        stickySizeText.setAttribute('data-sticky-size-text', '')
        parentLabel.textContent = ''
        parentLabel.appendChild(stickySizeText)
      }
      stickySizeText.textContent = `${optionName}: ${sizeValue}`
    }

    setTimeout(() => {
      this.isUpdatingStickyATC = false
    }, 100)
  }

  /**
   * Public method to get current size type
   */
  public getSizeType(): string {
    return this.selectedSizeType
  }

  /**
   * Public method to set size type programmatically
   */
  public setSizeType(sizeType: string): void {
    if (['US', 'UK', 'EU', 'CM'].includes(sizeType)) {
      this.changeSizeType(sizeType)
    }
  }

  /**
   * Public method to get selected variant ID
   */
  public getSelectedVariantId(): string | null {
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    const activeVariantButton = variantGridContainer?.querySelector('[data-variant-id].active') as HTMLElement
    return activeVariantButton?.dataset.variantId || null
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const globalDefaultSize = (window as any).product_default_size
  if (globalDefaultSize) {
    const selectors = document.querySelectorAll('universal-size-selector')
    selectors.forEach((selector) => {
      const element = selector as UniversalSizeSelector
      element.setSizeType(globalDefaultSize)
    })
  }
})

export default UniversalSizeSelector
